import React, {useEffect, useMemo, useState} from "react";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {dateComparator} from "../../../../../../../core/services/utils/utils";
import {Col} from "reactstrap";
import TryAgain from "../../../../../../components/app-specific/try-again";
import Button from "../../../../../../components/base/button/button";
import ProductNoteCard from "../../../../../../components/app-specific/product-cards/product-note-card";
import {Add} from '@mui/icons-material';
import UpsertProductNoteDialog from "../../../../../../components/dialogs/upsert-product-note";
import {useParams} from "react-router-dom";
import DateUtils from "../../../../../../../core/services/date-utils";

const initNewNote = {open: false, isForClient: false}

const ProductInformationNotesView = ({loading: loadingProps}) => {
    const params = useParams()
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [newNoteDialog, setNewNoteDialog] = useState(initNewNote)
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingNotes, [loadingProps, loadingNotes])

    const customerNotes = useMemo(() => notes
            ?.filter(e => e.isForClient)
            ?.sort((a, b) => dateComparator(b.submitDateTime, a.submitDateTime)),
        [notes])

    const privateNotes = useMemo(() => notes
            ?.filter(e => !e.isForClient)
            ?.sort((a, b) => dateComparator(b.submitDateTime, a.submitDateTime)),
        [notes])

    /**
     * With each change in the id in url params:
     * - get the notes belonging to the current product.
     */
    useEffect(() => {
        getProductNotes().then();
    }, [params.id])

    /**
     * Fetches the notes of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductNotes = async () => {
        setLoadingNotes(true);
        const response = await Api.getOperatorPartNotes(params.id)
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setNotes(response.data);
        }
        setLoadingNotes(false);
    }

    /**
     * Creates a new note with the provided values in the server.
     * @param note
     * @return {Promise<ApiResponseDs<*|null> | undefined>}
     */
    const createNewNote = async (note) => {
        const forApi = {
            title: note.title,
            msg: note.message,
            partId: parseInt(params.id),
            isForClient: note.isForClient
        }
        const response = await Api.createOperatorPartNote(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setNotes(prevState => [
                {
                    ...response.data ?? {},
                    submitDateTime: response.data?.submitDateTime ?? DateUtils.serverNow(),
                    submittedDateTime: response.data?.submitDateTime ?? DateUtils.serverNow(),
                },
                ...prevState,
            ])
        }
        return response;
    }

    /**
     * Updates the given note information in the server.
     * @param note
     * @return {Promise<ApiResponseDs<*|null> | undefined>}
     */
    const updateNote = async (note) => {
        const forApi = {
            id: note.id,
            title: note.title,
            msg: note.message,
            isForClient: note.isForClient,
        }
        const response = await Api.updateOperatorPartNote(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setNotes(prevState => prevState.map(e => e.id === note.id
                ? {...e, title: forApi.title, msg: forApi.msg}
                : e
            ))
        }
        return response;
    }

    /**
     * Removes the given note from the notes of the product in the server.
     * @param note
     * @return {Promise<void>}
     */
    const removeNote = async (note) => {
        const response = await Api.removeOperatorPartNote(note.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setNotes(prevState => prevState.filter(e => e.id !== note.id));
        }
    }

    return (
        <>
            <div className={'product-notes-view part-info-paper'}>
                <Col xs={12} lg={6} className={'d-flex flex-column'}>
                    <div className={'mt-3 mb-3 d-flex flex-wrap align-items-center justify-content-between'}>
                        <p className={'text-md black font-weight-semi-bold'}>
                            Customer-side Notes
                        </p>
                        <Button
                            variant={'text'}
                            color={'primary'}
                            className={'flex align-items-center justify-content-center'}
                            onClick={() => setNewNoteDialog({open: true, isForClient: true})}
                        >
                            <Add className={'mr-1'}/>
                            <p>
                                Add a new Note
                            </p>
                        </Button>
                    </div>
                    {
                        loading
                            ? Array(4).fill(null).map((_, e) => (
                                <div key={e} className={'w-100 loading extraLarge mt-3'}>
                                    <div/>
                                </div>
                            ))
                            : customerNotes?.length > 0
                                ? customerNotes?.map(note => (
                                    <ProductNoteCard
                                        key={note.id}
                                        note={note}
                                        update={updateNote}
                                        remove={removeNote}
                                    />
                                ))
                                : <div className={'my-5 py-5'}>
                                    <TryAgain
                                        text={'There are currently no customer-side notes submitted for this product.'}
                                    />
                                </div>
                    }
                </Col>
                <Col xs={12} lg={6} className={'d-flex flex-column'}>
                    <div className={'mt-3 mb-3 d-flex flex-wrap align-items-center justify-content-between'}>
                        <p className={'text-md black font-weight-semi-bold'}>
                            Private Notes
                        </p>
                        <Button
                            className='d-flex align-items-center justify-content-center'
                            variant={'text'}
                            color={'primary'}
                            onClick={() => setNewNoteDialog({open: true, isForClient: false})}
                        >
                            <Add className={'mr-1'}/>
                            <p>
                                Add a new Note
                            </p>
                        </Button>
                    </div>
                    {
                        loading
                            ? Array(4).fill(null).map((_, e) => (
                                <div key={e} className={'w-100 loading extraLarge mt-3'}>
                                    <div/>
                                </div>
                            ))
                            : privateNotes?.length > 0
                                ? privateNotes?.map(note => (
                                    <ProductNoteCard
                                        key={note.id}
                                        note={note}
                                        update={updateNote}
                                        remove={removeNote}
                                    />
                                ))
                                : <div className={'my-5 py-5'}>
                                    <TryAgain
                                        text={'There are currently no private notes submitted for this product.'}
                                    />
                                </div>
                    }
                </Col>
            </div>
            <UpsertProductNoteDialog
                isForClient={newNoteDialog.isForClient}
                open={newNoteDialog.open}
                close={() => setNewNoteDialog(initNewNote)}
                submit={createNewNote}
            />
        </>
    )
}


export default ProductInformationNotesView;
